import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { DataRole } from '@mode-switch/express';
import { Attr, Calc, Column } from '@mode-switch/model';
import { SelectResult } from '@mode-switch/query';
import { EMPTY_ENCODING, Model as VegasModel } from '@mode-vegas/vegas';
import { tokenFromVizID } from '@mode/charts/domain';
import {
  ChartFormatHelpers,
  EncodingHelpers,
  Flamingo,
  QueryBuilder,
  SwitchRenderModel,
  SwitchSchemaModel,
} from '@mode/charts/util-vegas';
import { LegacyReportFilterControl, ReportFilterControlFactory } from '@mode/filters/util';
import { NotebookRunState } from '@mode/legacy/types-app-state';
import { EMap, EmbedReportActions, EmbedReportState, InjectedState, ReportStore } from '@mode/reports/sandbox/contract';
import {
  AllFlags,
  ChartTypes,
  FeatureFlagsFacade,
  FlamingoResponse,
  FlamingoTypes,
  ReportTypes,
} from '@mode/shared/contract-common';
import { FlamingoClientService } from '@mode/shared/data-access-flamingo';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { DEFAULT_CHART_FORMAT, MetaTags, MetaTagsService, recordToJSObject } from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import * as angular from 'angular';
import * as $ from 'jquery';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import { BROWSER_LIMIT, FLAMINGO_UNSUPPORTED_CHARTS } from '../../../common/constants';
import { flamingoBackfillOnTheFlyColoring, updateChartLevelColoring } from '../../../lib/chart-coloring-helpers';
import { addReportFiltersToChartEncoding } from '../../../lib/chart-encoding-helpers';
import { loadFilteredDatasets } from '../../../services/report-dataset-service';
import { initialEmbedReportState } from '../domain/embed-report.reducers';
import { EmbedReportSelectors } from '../domain/embed-report.selectors';

const TIMEOUT = 3000;
const adapter = new FlamingoAdapter();

// Uncomment these to help with debugging locally
// const START_RENDER_TIME = Date.now();

// function logTimeSinceStart(message) {
//   console.log(`${message} - Time since start: ${(Date.now() - START_RENDER_TIME)/1000} sec`);
// }

function toHash<T>(arr: T[], reducer: (acc, next: T, index?: number) => void) {
  return arr.reduce((acc, next, index) => {
    reducer(acc, next, index);
    return acc;
  }, {});
}

class HeadlessViewerController {
  static $inject = [
    '$window',
    '$http',
    '$scope',
    'RxStoreService',
    'FlamingoClient',
    'FeatureFlagsFacade',
    'MetaTagsService',
    'BugsnagService',
  ];

  initialLayout: string;

  // Local store related to building state
  flamingoVizItems: any[];
  flamingoResults: SelectResult<DataRole>[];

  fullWidthReport$: Observable<boolean>;
  reportFilterControls$: Observable<any>;

  constructor(
    private $window: angular.IWindowService,
    private $http: angular.IHttpService,
    private $scope: angular.IScope,
    private rxStore: ReportStore,
    private flamingoClient: FlamingoClientService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private metaTagsService: MetaTagsService,
    private bugsnag: BugsnagService
  ) {
    this.fullWidthReport$ = rxStore.select(EmbedReportSelectors.fullWidth);
    this.reportFilterControls$ = rxStore.select(EmbedReportSelectors.reportFilterControls);

    $window.dispatchAction = (action) => {
      rxStore.dispatch(action);
      $scope.$evalAsync();
    };
  }

  $onInit() {
    // initialize the FeatureFlagsFacade in case it blocks report rendering
    this.featureFlagsFacade.init({} as AllFlags);

    if (typeof this.$window.callPhantom === 'function') {
      const status: any | Promise<any> = this.$window.callPhantom({
        command: 'ready',
      });

      if (status instanceof Promise) {
        // handle picasso/puppeteer Promise<status>
        status.then((s) =>
          this.injectState(s)
            .then(this.capturePdf)
            .catch((err) => {
              this.reportError(err);
              console.error('Error loading data due to', err);
              window.callPhantom({
                command: 'exit',
                payload: {
                  error: err,
                  msg: err.message,
                  stack: err.stack,
                },
              });
            })
        );
      } else {
        // handle phantomjs status
        this.injectState(status)
          .then(this.capturePdf)
          .catch((err) => {
            this.reportError(err);
            console.error('Error loading data due to', err);
          });
      }
    }
  }

  reportError(err: Error) {
    this.bugsnag.notify(err, (event) => {
      event.errors[0].errorClass = `ReportCaptureError`;
    });
  }

  capturePdf() {
    setTimeout(function () {
      console.info('capturing pdf...');
      const height = $('body').outerHeight(true);
      window.callPhantom({
        command: 'capture',
        height: height,
      });
    }, TIMEOUT);
  }

  downloadDatasets(results: InjectedState.InjectedData['results']) {
    return Promise.all(
      results
        .sort((a, b) =>
          a.query_created_at > b.query_created_at ? 1 : a.query_created_at === b.query_created_at ? 0 : -1
        )
        .map<PromiseLike<EmbedReportState.EmbedDataset>>((result) => {
          const oversized = BROWSER_LIMIT < result.content_length;
          const dataset: EmbedReportState.EmbedDataset = {
            token: result.token,
            columns: oversized ? [] : result.columns,
            count: result.count,
            bytes: result.bytes,
            csv: result.csv,
            reportQueryUrl: result.reportQueryUrl,
            reportDataUrl: result.reportDataUrl,
            state: result.state,
            queryName: result.queryName,
            query_token: result.query_token,
            content: [],
            oversized,
            content_length: result.content_length,
          };
          if (result.state === 'succeeded' && dataset.oversized === false) {
            return this.$http.get(result.url).then(
              function (response) {
                dataset.content = response.data as any[];
                return dataset;
              },
              function (res) {
                console.error(
                  `FAILED to download dataset ${result.dataset_token} from ${result.url}: ${JSON.stringify(
                    res,
                    null,
                    2
                  )}`
                );
                dataset.state = 'failed';
                return dataset;
              }
            );
          } else {
            return Promise.resolve(dataset);
          }
        })
    );
  }

  async filterDatasets(
    datasets: EmbedReportState.EmbedDataset[],
    filters: ReportTypes.ReportFilterControl[]
  ): Promise<EmbedReportState.EmbedDataset[]> {
    const reportModels = datasets.map((dataset) => ({
      id: dataset.query_token,
      token: dataset.query_token,
      underBrowserSizeLimit: true,
      query_token: dataset.query_token,
      queryName: dataset.queryName || 'Query',
      state: 'succeeded',
      reportDataUrl: dataset.reportDataUrl,
      reportQueryUrl: dataset.reportQueryUrl,
      content_length: dataset.content_length,
      csv: dataset.csv,
      json: undefined, // not used here anyway
      oversized: BROWSER_LIMIT < dataset.content_length,

      content: dataset.content,
      columns: dataset.columns,
      count: dataset.count,
      bytes: dataset.bytes,

      model: VegasModel.build(VegasModel.convertModeSchema(dataset.columns || []), dataset.content),
    }));

    // We get back a list of immutable ReportDataset objects.
    // We need to copy them back to plain javascript objects.
    // Eventually these immutable versions should be removed entirely.
    const wrappedDatasets = await loadFilteredDatasets(reportModels, filters);

    datasets = wrappedDatasets.map((reportDataset) => ({
      token: reportDataset.token,
      columns: reportDataset.columns,
      content: reportDataset.content,
      content_length: reportDataset.content_length,
      count: reportDataset.count,
      bytes: reportDataset.bytes,
      csv: reportDataset.csv,
      oversized: reportDataset.oversized,
      reportQueryUrl: reportDataset.reportQueryUrl,
      reportDataUrl: reportDataset.reportDataUrl,
      state: reportDataset.state,
      queryName: reportDataset.queryName,
      query_token: reportDataset.query_token,
    }));

    return datasets;
  }

  buildChartState(
    chartHash: InjectedState.InjectedData['report']['chart_view'],
    datasets: EmbedReportState.EmbedDataset[]
  ) {
    const charts = Object.keys(chartHash).map((id) => {
      const chartView = chartHash[id];
      if (chartView._color_palette) {
        if ('selectedChart' in chartView) {
          if (chartView.selectedChart === 'bigNumber') {
            chartView.bigNumber.colors = chartView._color_palette.split(',');
          }
        } else if (chartView.format) {
          chartView.format = {
            ...chartView.format,
            colors: {
              ...chartView.format.colors,
              custom: chartView._color_palette.split(','),
            },
          };
        }
      }

      if ('encoding' in chartView && chartView.encoding != null) {
        if (!chartView.format) {
          // [Han] I'm not sure why we don't set .custom = ._color_palette in this case,
          //       but I don't want to change the logic from before: https://git.io/vxOra
          chartView.format = angular.copy(DEFAULT_CHART_FORMAT);
        }
      }

      return {
        token: tokenFromVizID(id),
        ...('encoding' in chartView
          ? {
              chartTitle: chartView.title,
              description: chartView.description,
              caption: chartView.caption,
              chartType: chartView.chartType,
              encoding: chartView.encoding,
              format: chartView.format,
              fieldFormats: chartView.fieldFormats,
              fieldColors: chartView.fieldColors,
              facetSizes: chartView.facetSizes,
              isAutoUpdateDisabled: chartView.isAutoUpdateDisabled ?? false,
              v1View: {},
              version: 2,
            }
          : {
              chartTitle: chartView.chartTitle,
              description: null,
              caption: ChartTypes.EMPTY_CAPTION,
              chartType: chartView.selectedChart,
              encoding: EMPTY_ENCODING,
              format: angular.copy(DEFAULT_CHART_FORMAT),
              fieldFormats: {},
              fieldColors: {},
              facetSizes: {},
              isAutoUpdateDisabled: false,
              v1View: chartView,
              version: 1,
            }),
        colorPaletteToken: chartView._color_palette, // placeholder because token isn't injected
        queryToken: chartView._dataset,
      };
    });

    return charts;
  }

  buildViewFromQueryRun(queryRunResource: InjectedState.QueryRunResult): any {
    console.info('Entering buildViewFromQueryRun', queryRunResource.token);
    const attrs: Array<FlamingoTypes.ModeAttr> = [];
    const { token, view, columns, query_token } = queryRunResource;

    if (view != null) {
      for (const attrResource of view.attrs) {
        const { name, type, role, scale } = attrResource;
        const { formula_source, formula_type, formula_role } = attrResource;

        switch (attrResource.source) {
          case Attr.Source.Calc: {
            attrs.push(Calc.make(name, formula_source, type, role, scale, formula_type, formula_role));
            break;
          }
          case Attr.Source.Column: {
            attrs.push(Column.make(name, formula_source, type, role, scale, formula_type, formula_role));
            break;
          }
        }
      }
    }

    console.info('Exiting buildViewFromQueryRun');
    return Switch.Views.make(query_token, token, columns, attrs);
  }

  buildFlamingoViews(queryResults: InjectedState.InjectedData['results']) {
    console.info('Entering buildFlamingoViews');
    let viewsObject: EMap<FlamingoTypes.ModeView>;
    queryResults.forEach((result) => {
      viewsObject = {
        ...viewsObject,
        [result.query_token]: this.buildViewFromQueryRun(result),
      };
    });

    console.info('Exiting buildFlamingoViews');
    return viewsObject;
  }

  async buildFlamingoState(
    chartHash: InjectedState.InjectedData['report']['chart_view'],
    queryResults: InjectedState.InjectedData['results'],
    layout: string,
    flamingoHost: string,
    filters: LegacyReportFilterControl[]
  ): Promise<EmbedReportState.SerializedReportState['flamingo']> {
    // Iterate over results and build the views. We need an array for building the plan and a keyed object for the state.
    const viewsObject: EMap<FlamingoTypes.ModeView> = this.buildFlamingoViews(queryResults);
    const viewsArray = queryResults.map((result) => this.buildViewFromQueryRun(result));

    // Build the model which contains views of all queries
    const model: FlamingoTypes.ModeModel = {
      name: null,
      params: [],
      views: viewsArray,
    };

    // Track data we need to build the state for each viz and match it up with appopriate query results once finished
    this.flamingoVizItems = [];

    // Filter for just viz ids used in the report view (we are passed all ids in the report)
    const reportChartIds = Object.keys(chartHash).filter((id) => layout.includes(id));

    console.info('Build flamingo queries');
    // Make flamingo queries
    const flamingoQueries = reportChartIds.map((id) => {
      // Get all data necessary for generating the plan and running the flamingo query
      const chartView = chartHash[id],
        chartToken = tokenFromVizID(id),
        queryToken = chartView._dataset,
        queryResult = queryResults.find((result) => result.query_token === queryToken),
        queryRunToken = queryResult.token;

      if ('encoding' in chartView) {
        console.info('Building query for', id);
        // If the encoding is incomplete, no need to execute a plan for it
        if (EncodingHelpers.isChartEncodingComplete(chartView.encoding, chartView.chartType)) {
          console.info('Chart encoding is complete');
          // Check type to determine how to build the plan appropriately
          // Note, this skips any unsupported chart types entirely
          const schemaModel = SwitchSchemaModel.make(model, queryToken);

          console.info('Validate filters');
          const validFilters = Flamingo.getValidReportFilters(model, queryToken, filters);

          if (
            chartView.chartType === 'table' &&
            EncodingHelpers.validateTableEncodingFields(chartView.encoding, schemaModel).length <= 0
          ) {
            console.info('Chart is a valid table encoding');
            const filteredEncoding = addReportFiltersToChartEncoding(undefined, chartView.encoding, validFilters);
            const plan = QueryBuilder.buildRecordsPlan(adapter, model, queryToken, filteredEncoding, 100, 0);

            this.flamingoVizItems.push({
              chartToken,
              plan,
              chartType: chartView.chartType,
            });

            console.info(`Executing plan. ID: ${plan.id}`);
            // Make the table query, catch any error here
            return this.flamingoSelect(
              chartView._switch_view.flamingo_signature,
              flamingoHost,
              queryRunToken,
              plan
            ).catch((error) => {
              console.error(
                `${chartView.chartType} with queryRunToken "${queryRunToken}" has failed with the following error: ${error}`
              );
            });
          } else if (
            FLAMINGO_UNSUPPORTED_CHARTS.indexOf(chartView.chartType) < 0 &&
            EncodingHelpers.validateChartEncodingFields(chartView.encoding, schemaModel).length <= 0
          ) {
            console.info('Chart is a valid chart encoding');
            const filteredEncoding = addReportFiltersToChartEncoding(undefined, chartView.encoding, validFilters);
            const needsColorBackfill = 'format' in chartView && chartView.format.colors.map == null;
            const includeSortDomain = ChartFormatHelpers.useMuzeRenderer(chartView.chartType, chartView.format);
            const plan = QueryBuilder.buildPivotQuery(
              adapter,
              model,
              queryToken,
              filteredEncoding,
              needsColorBackfill,
              includeSortDomain
            );

            this.flamingoVizItems.push({
              chartToken,
              plan,
              chartType: chartView.chartType,
            });

            console.info(`Executing plan. ID: ${plan.id}, ColorBackfill: ${needsColorBackfill}`);
            // Make the chart query, catch any error here
            return this.flamingoSelect(
              chartView._switch_view.flamingo_signature,
              flamingoHost,
              queryRunToken,
              plan
            ).catch((error) => {
              console.error(
                `${chartView.chartType} with queryRunToken "${queryRunToken}" has failed with the following error: ${error.message}`
              );
              if (error.stack) {
                console.error('Stack: ', error.stack);
              }
            });
          }
        }
      }

      console.warn('Not a flamingo-enabled chart', id);
      return Promise.resolve(null);
    });

    // Wait for all queries to resolve, filter out legacy charts (null) and individual failed queries (undefined).
    // This means flamingo results will never fail as we've already caught any errors, but can potentially be empty.
    // Failed flamingo queries will currently render an empty viz.
    this.flamingoResults = await Promise.all(flamingoQueries);
    this.flamingoResults = this.flamingoResults.filter((item) => !isNil(item));

    console.info('Flamingo queries complete, query count:', this.flamingoResults.length);
    // Initialize the flamingo serialized feature state object
    const flamingoState: EmbedReportState.SerializedReportState['flamingo'] = {
      pivotsByViz: {},
      recordsByViz: {},
      columnsByViz: {},
      statesByViz: {},
      errorsByViz: {},
      views: viewsObject,
      dataProfiling: {},
      conditionalFormatting: {},
    };

    // Iterate over our flamingo based viz items, grab corresponding query result (if exists), and build appropriate state
    this.flamingoVizItems.forEach((item) => {
      if (item.chartType === 'table') {
        const currentResult = this.flamingoResults.find((result) => result.id === item.plan.id);
        if (currentResult) {
          const count = Flamingo.getCountValue(item.plan, currentResult);
          const recordsResult = Flamingo.getRecordsInline(item.plan, currentResult);

          flamingoState.recordsByViz = {
            ...flamingoState.recordsByViz,
            [item.chartToken]: {
              content: recordsResult.items,
              count: count,
            },
          };

          flamingoState.columnsByViz = {
            ...flamingoState.columnsByViz,
            [item.chartToken]: recordsResult.columns,
          };

          flamingoState.statesByViz = {
            ...flamingoState.statesByViz,
            [item.chartToken]: FlamingoTypes.PlanStatus.Succeeded,
          };
        } else {
          flamingoState.statesByViz = {
            ...flamingoState.statesByViz,
            [item.chartToken]: FlamingoTypes.PlanStatus.Failed,
          };
        }
      } else {
        const currentResult = this.flamingoResults.find((result) => result.id === item.plan.id);
        if (currentResult) {
          flamingoState.pivotsByViz = {
            ...flamingoState.pivotsByViz,
            [item.chartToken]: {
              plan: item.plan,
              result: currentResult,
            },
          };

          flamingoState.statesByViz = {
            ...flamingoState.statesByViz,
            [item.chartToken]: FlamingoTypes.PlanStatus.Succeeded,
          };
        } else {
          flamingoState.statesByViz = {
            ...flamingoState.statesByViz,
            [item.chartToken]: FlamingoTypes.PlanStatus.Failed,
          };
        }

        // TODO: add flamingo error map
      }
    });

    console.info('Flamingo state built');
    return flamingoState;
  }

  // Fill flamingo colors in the formats of present flamingo powered charts
  fillFlamingoColors(
    flamingoVizItems,
    flamingoResults: SelectResult<DataRole>[],
    chartHash: InjectedState.InjectedData['report']['chart_view'],
    queryResults: InjectedState.InjectedData['results'],
    state: EmbedReportState.SerializedReportState
  ) {
    console.info('Begin fillFlamingoColors');
    const formats = {};

    // Build the model
    const viewsArray = queryResults.map((result) => this.buildViewFromQueryRun(result));
    const model: FlamingoTypes.ModeModel = {
      name: null,
      params: [],
      views: viewsArray,
    };

    // Filter out tables as we don't need to process them
    const filteredFlamingoVizItems = flamingoVizItems.filter((vizItem) => vizItem.chartType !== 'table');
    filteredFlamingoVizItems.forEach((vizItem) => {
      const chartToken = vizItem.chartToken;
      const chartView = chartHash[`chart_${chartToken}`];
      const encoding = state.charts.encodings[chartToken];
      let format: ChartTypes.ChartFormat;

      // This syntax seems necessary to access format on chartView
      if ('format' in chartView) {
        format = chartView.format;
      } else {
        return;
      }

      // Now we do the same process we do on plan success
      // 1. backfill on the fly
      // 2. update chart level coloring
      // 3. TODO: add report level coloring to this flow
      // Do backfill on the fly in case it is necessary necessary
      const schemaModel = SwitchSchemaModel.make(model, vizItem.plan.viewName);
      const currentResult = flamingoResults.find((result) => result.id === vizItem.plan.id);
      if (currentResult == null) {
        // See: Cannot read property 'results' of undefined
        // https://app.bugsnag.com/mode-analytics/webapp-frontend/timeline?filters[event.since]=30d&filters[error.status]=open&filters[search]=reportcaptureerror&sort=last_seen
        console.info(
          `Could not find flamingo result for Chart<${chartToken}>, bailing on Backfill on-the-fly coloring`
        );
        return;
      }

      console.info('Backfill on-the-fly coloring');
      format = {
        ...format,
        colors: {
          ...format.colors,
          map: flamingoBackfillOnTheFlyColoring(format, schemaModel, encoding, vizItem.plan, currentResult),
        },
      };

      // Do chart level consistency coloring update
      const switchRenderModel = SwitchRenderModel.make(model, vizItem.plan.viewName, vizItem.plan, currentResult);
      const newSeriesNames = switchRenderModel.getColorDomain(encoding, format);

      console.info('Update chart level coloring');
      format = {
        ...format,
        colors: {
          ...format.colors,
          map: updateChartLevelColoring(format, newSeriesNames, encoding),
        },
      };

      formats[chartToken] = format;
    });

    console.info('End fillFlamingoColors');
    return formats;
  }

  /**
   * Make the flamingo call. This is essentially the `select` method from `flamingo-service.ts`, but required manually
   * setting the host in the phantom context. This will likely need to be updated if the corresponding service is updated.
   *
   * Note: These queries will likely already be ingested, but it's possible they could already have been reaped. If that is
   * the case, they will run and be ingested here, which could take a long time for very large queries and potentially hit
   * the max render time set `in phantomjs_imager.js`.
   */
  async flamingoSelect(
    signature: string,
    host: string,
    datasetName: string,
    plan: FlamingoTypes.ModeSelectPlan
  ): Promise<FlamingoTypes.ModeSelectResult> {
    const readyResponse = await this.flamingoClient.ready(host, signature, datasetName, false);
    switch (readyResponse.type) {
      case FlamingoResponse.ResponseType.ReadyCommitted: {
        const selectResponse = await this.flamingoClient.select(host, signature, datasetName, plan, false);

        switch (selectResponse.type) {
          case FlamingoResponse.ResponseType.SelectSuccess: {
            return { ...selectResponse.result, id: plan.id };
          }
          case FlamingoResponse.ResponseType.DatasetNotFound:
          case FlamingoResponse.ResponseType.SignatureExpired:
          default:
            return Promise.reject(selectResponse);
        }
      }
      default:
        return Promise.reject(readyResponse);
    }
  }

  downloadNotebookVisuals(visuals: InjectedState.InjectedData['report']['python_visualizations']) {
    return Promise.all(
      visuals.map((v) =>
        v.state === 'none' || v.state === 'succeeded'
          ? Promise.all(
              v.results.map<PromiseLike<InjectedState.VizCellResult>>((r) => {
                if (r.result_type === 'image') {
                  return Promise.resolve({
                    token: v.token,
                    content: r.result_link,
                    result_type: r.result_type,
                    content_type: r.content_type,
                    state: v.state,
                  });
                } else {
                  return this.$http
                    .get(r.result_link)
                    .then((response) => ({
                      token: v.token,
                      content: response.data as string,
                      result_type: r.result_type,
                      content_type: r.content_type,
                      state: v.state,
                    }))
                    .catch(() => ({
                      token: v.token,
                      content: undefined,
                      result_type: r.result_type,
                      content_type: r.content_type,
                      state: 'failed',
                    }));
                }
              })
            )
          : Promise.resolve([] as InjectedState.VizCellResult[])
      )
    );
  }

  initBugsnag(key: string, stage: string, context: string) {
    try {
      this.bugsnag.start({
        apiKey: key,
        autoTrackSessions: true,
        releaseStage: stage,
        maxBreadcrumbs: 40,
        appType: 'report_capture',
        collectUserIp: false,
        context,
        autoDetectErrors: false, // want to avoid logging errors from custom scripts, but does mean logging is manual
      });
    } catch (err) {
      console.info('Unable to start bugsnag');
    }
  }

  async buildSerializedReportState(data: InjectedState.InjectedData) {
    const report = data.report;
    this.initialLayout = report.layout;
    const bugsnagApiKey = report.bugsnag_api_key;

    if (bugsnagApiKey != null) {
      this.initBugsnag(bugsnagApiKey, report.bugsnag_release_stage, report.web_url);
    }

    // Copy the initial state since this function mutates the state in place to make it easier to build
    const state: EmbedReportState.SerializedReportState = angular.copy(initialEmbedReportState.serializedReport);
    state.report = {
      token: report.token,
      layout: report.layout,
      name: report.title,
      description: report.description,
      fullWidth: report.full_width,
      theme: {
        href: report.theme.css_href,
        customSource: report.theme.css_source,
      },
      drillAnywhereEnabled: report.drill_anywhere_enabled,
      hasImportedDatasets: report.has_imported_datasets,
    };

    const filterControlRecords = report.filters.map((f) =>
      recordToJSObject<ReportTypes.ReportFilterControl>(ReportFilterControlFactory.buildFromResource(f))
    );
    state.filters = {
      // just for posterity, don't think it's necessary to store them
      controls: filterControlRecords,
      options: {},
      all: {},
      totalOptionCount: {},
      selectedOptionCount: {},
    };

    state.tables = Object.keys(report.table_view).reduce((acc, id) => {
      const token = tokenFromVizID(id);
      acc[token] = {
        token: token,
        view: report.table_view[id],
        queryToken: report.table_view[id]._dataset,
        runLink: '',
        vizLink: '',
      };
      return acc;
    }, {});

    const rawDatasets = await this.downloadDatasets(data.results);
    console.info('datasets loaded');
    const filteredDatasets = await this.filterDatasets(rawDatasets, filterControlRecords);

    // merge filtered datasets into the state tree
    state.localQueryTokensSortedByCreationDate = filteredDatasets.map((ds) => ds.query_token);
    state.legacyEmbedDatasets = toHash(filteredDatasets, (acc, ds) => (acc[ds.query_token] = ds));

    state.queryRunStatus = toHash(
      filteredDatasets,
      (acc, ds) =>
        (acc[ds.query_token] = {
          token: ds.query_token,
          runToken: '', // not available or not needed
          dbtMetricToken: '', // not needed
          isMetric: false, // not needed
          state: ds.state,
          datasetLoaded: true,
        })
    );

    const charts = this.buildChartState(data.report.chart_view, filteredDatasets);
    state.charts = {
      tokens: charts.map((c) => c.token),
      chartTypes: toHash(charts, (acc, c) => (acc[c.token] = c.chartType)),
      colorPaletteTokens: toHash(charts, (acc, c) => (acc[c.token] = c.colorPaletteToken)),
      encodings: toHash(charts, (acc, c) => (acc[c.token] = c.encoding)),
      formats: toHash(charts, (acc, c) => (acc[c.token] = c.format)),
      fieldFormats: toHash(charts, (acc, c) => (acc[c.token] = c.fieldFormats)),
      fieldColors: toHash(charts, (acc, c) => (acc[c.token] = c.fieldColors)),
      facetSizes: toHash(charts, (acc, c) => (acc[c.token] = c.facetSizes)),
      histories: toHash(charts, (acc, c) => (acc[c.token] = { undoStack: [], redoStack: [] })), // no history needed for static rendering
      queryTokens: toHash(charts, (acc, c) => (acc[c.token] = c.queryToken)),
      titles: toHash(charts, (acc, c) => (acc[c.token] = c.chartTitle)),
      descriptions: toHash(charts, (acc, c) => (acc[c.token] = c.description)),
      captions: toHash(charts, (acc, c) => (acc[c.token] = c.caption)),
      isAutoUpdateDisabled: toHash(charts, (acc, c) => (acc[c.token] = c.isAutoUpdateDisabled)),
      v1Views: toHash(charts, (acc, c) => (acc[c.token] = c.v1View)),
      versions: toHash(charts, (acc, c) => (acc[c.token] = c.version)),
      runLinks: {},
      vizLinks: {},
    };

    // Build flamingo state
    let filters: LegacyReportFilterControl[];
    filters = report.filters.map((filter) => ReportFilterControlFactory.buildFromResource(filter));
    const flamingoState = await this.buildFlamingoState(
      data.report.chart_view,
      data.results,
      data.report.layout,
      data.report.flamingo_host,
      filters
    );
    state.flamingo = flamingoState;

    // Fill colors for flamingo chart formats
    const updatedFlamingoColorsFormats = this.fillFlamingoColors(
      this.flamingoVizItems,
      this.flamingoResults,
      data.report.chart_view,
      data.results,
      state
    );
    state.charts.formats = {
      ...state.charts.formats,
      ...updatedFlamingoColorsFormats,
    };

    if (report.python_visualizations) {
      const notebookState: EmbedReportState.NotebookViz = {
        vizTokens: report.python_visualizations.map((viz) => viz.token),
        runState: NotebookRunState.Succeeded,
        titles: toHash(report.python_visualizations, (acc, viz) => (acc[viz.token] = viz.title)),
        // just need to put something here so viz state selector will return success
        cellTokens: toHash(report.python_visualizations, (acc, viz) => (acc[viz.token] = viz.token)),
        contents: {},
        resultTypes: {},
        dates: {},
        missingResults: {},
        contentTypes: {},
        vizRunLinks: {},
        vizLinks: {},
      };

      const visuals = await this.downloadNotebookVisuals(data.report.python_visualizations);
      notebookState.contents = toHash(
        visuals,
        (acc, next, ii) => (acc[report.python_visualizations[ii].token] = next.map((n) => n.content))
      );
      notebookState.resultTypes = toHash(
        visuals,
        (acc, next, ii) => (acc[report.python_visualizations[ii].token] = next.map((n) => n.result_type))
      );
      notebookState.contentTypes = toHash(
        visuals,
        (acc, next, ii) => (acc[report.python_visualizations[ii].token] = next.map((n) => n.content_type))
      );

      state.notebookVisuals = notebookState;
    }

    return state;
  }

  initMetaService(data: InjectedState.InjectedData) {
    this.metaTagsService.loadFromJson({
      [MetaTags.ReportOwnerUsername]: data.report?.account.username,
      [MetaTags.ReportRunEntryPath]: data.report?.web_url,
    });
  }

  initReportStyle(data: InjectedState.InjectedData) {
    const style = document.createElement('link');
    style.id = 'theme';
    style.rel = 'stylesheet';
    style.href = data.report.theme.css_href;
    this.$window.document.head.appendChild(style);
  }

  async injectState(data: InjectedState.InjectedData) {
    this.initMetaService(data);
    this.initReportStyle(data);

    const serializedReportState = await this.buildSerializedReportState(data);

    this.$scope.$evalAsync(() => {
      this.rxStore.dispatch(new EmbedReportActions.LoadThemeSuccess());
      this.rxStore.dispatch(new EmbedReportActions.LoadReportFeatureState(serializedReportState));
    });
  }
}

export const HeadlessViewerComponent: angular.IComponentOptions = {
  bindings: {},
  controller: HeadlessViewerController,
  controllerAs: 'ctrl',
  template: `
    <div id="content-root" ng-class="{'full-width-report': (ctrl.fullWidthReport$ | async:this)}">
      <embed-layout data-target="embed-layout" initial-layout="ctrl.initialLayout" is-capture-mode="true" ng-if="ctrl.initialLayout" class="mode-content"></embed-layout>
    </div>`,
};
